import { ReactNode } from "react";
import { Link } from "react-router-dom";

import { Avatar } from "../../components/Avatar/Avatar";
import { EmptyState } from "../../components/EmptyState/EmptyState";
import { Pill } from "../../components/Pill/Pill";
import { Skeleton } from "../../components/Skeleton/Skeleton";
import { Table, type Column } from "../../components/Table/Table";

import { formatDay, formatWhen } from "./formatTime";
import type {
  PlayerReferralsResponse,
  ReferralInvitedPlayer,
  ReferralInviter,
} from "./types";
import styles from "./PlayerReferralsCard.module.css";

// === Component ====================================================== //

interface Props {
  projectId: string;
  /** The answer, or undefined while the request is in flight. */
  data: PlayerReferralsResponse | undefined;
  loading: boolean;
  failed: boolean;
}

/**
 * Who brought this player in, whom this player brought in, and how far each of
 * those pairs got towards the reward.
 *
 * Read-only, like the Gifts card above it: an invite is a thing that happened,
 * and there is no endpoint here to undo one or to pay it out by hand.
 *
 * The query lives on the page, so this card is drawn from props alone — and,
 * like its neighbours, it draws its own loading and failure states inside the
 * body so neither ever holds back the editor below.
 */
export function PlayerReferralsCard({
  projectId,
  data,
  loading,
  failed,
}: Props) {
  let body: ReactNode;
  let sub: ReactNode = null;

  if (failed && !data) {
    body = (
      <div className={styles.note} role="status">
        Couldn&apos;t load invites.
      </div>
    );
  } else if (loading || !data) {
    body = <ReferralsSkeleton />;
  } else {
    // The rule the server counted under, not the one the config holds now: a
    // threshold edited an hour ago would otherwise re-label rows that were
    // decided by the old one. Zero or a blank key is a project where the
    // trigger was never set — there is no goal to print a progress against.
    const triggerSet = data.trigger_stat_key !== "" && data.trigger_value > 0;
    const { invited } = data;
    // A ladder now, or steps this player took under one before it was emptied:
    // either way there are steps to show. Neither — no figure and no column,
    // since nobody's friends were ever counted toward one.
    const hasLadder = data.ladder_set || data.step > 0;

    sub = (
      <span className={styles.cardSub}>
        {triggerSet
          ? `Trigger · ${data.trigger_stat_key} ${data.trigger_value}`
          : "No trigger"}
      </span>
    );

    const columns: Column<ReferralInvitedPlayer>[] = [
      {
        key: "player",
        header: "Player",
        cell: (row) => <InvitedParty projectId={projectId} row={row} />,
      },
      {
        key: "accepted",
        header: "Accepted",
        width: "150px",
        cell: (row) => (
          <span className={styles.when}>{formatWhen(row.accepted_at)}</span>
        ),
      },
      {
        key: "progress",
        header: "Progress",
        width: "120px",
        cell: (row) => (
          <span className={styles.progress}>
            {triggerSet && row.progress !== null ? (
              <>
                {row.progress}{" "}
                <span className={styles.goal}>/ {data.trigger_value}</span>
              </>
            ) : (
              "—"
            )}
          </span>
        ),
      },
      ...(hasLadder
        ? [
            {
              key: "step",
              header: "Step",
              width: "80px",
              cell: (row: ReferralInvitedPlayer) =>
                row.step === null ? (
                  <span className={`${styles.stepNo} ${styles.stepNoNone}`}>
                    —
                  </span>
                ) : (
                  <span className={styles.stepNo}>#{row.step}</span>
                ),
            },
          ]
        : []),
      {
        key: "reward",
        header: "Reward",
        width: "150px",
        cell: (row) => <RewardPill row={row} />,
      },
    ];

    body = (
      <>
        {data.inviter && (
          <InvitedBy
            projectId={projectId}
            inviter={data.inviter}
            statKey={data.trigger_stat_key}
            triggerValue={data.trigger_value}
            triggerSet={triggerSet}
            divided={invited.total > 0}
          />
        )}

        {invited.total > 0 && (
          <>
            <div
              className={`${styles.summary}${
                data.inviter ? "" : ` ${styles.summaryLead}`
              }${hasLadder ? "" : ` ${styles.summaryThree}`}`}
            >
              <div className={styles.sumCell}>
                <div className={styles.sumKey}>Invited</div>
                <div className={styles.sumVal}>{invited.total}</div>
              </div>
              <div className={styles.sumCell}>
                <div className={styles.sumKey}>Rewarded</div>
                <div className={styles.sumVal}>{invited.rewarded}</div>
              </div>
              <div className={styles.sumCell}>
                <div className={styles.sumKey}>Waiting</div>
                <div className={styles.sumVal}>{invited.waiting}</div>
              </div>
              {/* The same number the game gets from /v1/invites/invited. It
                  can sit below Rewarded: a pair settled while there was no
                  ladder took no step. */}
              {hasLadder && (
                <div className={styles.sumCell}>
                  <div className={styles.sumKey}>Ladder step</div>
                  <div className={styles.sumVal}>{data.step}</div>
                </div>
              )}
            </div>

            <div className={styles.hint}>
              {hasLadder ? (
                <>
                  The last 20 players who accepted this player&apos;s code.{" "}
                  <b>Step</b> is the order in which they reached the trigger,
                  and <b>Reward</b> is what this player got for it —{" "}
                  <b>No reward</b> is a step the ladder paid nothing for when it
                  was reached.
                </>
              ) : (
                <>
                  The last 20 players who accepted this player&apos;s code, and
                  what this player got for each of them.
                </>
              )}
            </div>

            <div className={styles.tableWrap}>
              <Table columns={columns} rows={invited.items} />
            </div>
          </>
        )}

        {/* Neither side of the mechanic touched this player. Said in one
            sentence rather than as an empty table under four zeroes, which
            would read as a list that failed to load. */}
        {!data.inviter && invited.total === 0 && (
          <EmptyState
            title="No invites yet"
            description="This player accepted nobody's code, and nobody accepted theirs."
          />
        )}
      </>
    );
  }

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <h3 className={styles.cardTitle}>Invites</h3>
        {sub}
      </div>
      <div className={styles.cardBody}>{body}</div>
    </div>
  );
}

// === "Invited by" ==================================================== //

interface InvitedByProps {
  projectId: string;
  inviter: ReferralInviter;
  statKey: string;
  triggerValue: number;
  triggerSet: boolean;
  /** Whether a list follows, and the line therefore closes with a rule. */
  divided: boolean;
}

/**
 * The player who brought this one in, and where their pair stands.
 *
 * The invited side is paid by the trigger itself, so `reward_granted_at` is
 * the whole verdict here: with it, this player has been paid and the line
 * reports the day; without it, the trigger has not been reached yet and the
 * line reports how far off it is.
 */
function InvitedBy({
  projectId,
  inviter,
  statKey,
  triggerValue,
  triggerSet,
  divided,
}: InvitedByProps) {
  const granted = inviter.reward_granted_at !== null;
  // Deleted since: the server sends neither an id to link to nor a name to
  // print, and the two go together.
  const gone = inviter.player_id === null || inviter.display_name === null;
  // A figure quoted against a goal that is not configured says nothing.
  const showProgress = triggerSet && inviter.progress !== null;

  return (
    <div
      className={`${styles.inviter}${divided ? ` ${styles.inviterDivided}` : ""}`}
    >
      {gone ? (
        <span className={styles.goneAvatar} aria-hidden>
          ?
        </span>
      ) : (
        <Avatar name={inviter.display_name ?? undefined} size={38} />
      )}

      <div className={styles.inviterMain}>
        <div className={styles.kicker}>Invited by</div>
        <div className={styles.inviterName}>
          {gone ? (
            <span className={styles.gone}>Deleted player</span>
          ) : (
            <Link to={`/projects/${projectId}/players/${inviter.player_id}`}>
              {inviter.display_name}
            </Link>
          )}
          <span className={styles.code}>{inviter.code}</span>
        </div>
        <div className={styles.inviterSub}>
          {`Code accepted ${formatWhen(inviter.accepted_at)}`}
          {granted && inviter.trigger_reached_at !== null && (
            <>
              {` · trigger reached ${formatDay(inviter.trigger_reached_at)}`}
              {showProgress && (
                <>
                  {" at "}
                  <span className={styles.mono}>
                    {`${statKey} ${inviter.progress}`}
                  </span>
                </>
              )}
            </>
          )}
          {!granted && showProgress && (
            <>
              {" · now at "}
              <span className={styles.mono}>
                {`${statKey} ${inviter.progress} / ${triggerValue}`}
              </span>
            </>
          )}
        </div>
      </div>

      {granted ? (
        <Pill variant="success">
          Reward granted · {formatDay(inviter.reward_granted_at)}
        </Pill>
      ) : (
        <Pill variant="neutral">Waiting for the trigger</Pill>
      )}
    </div>
  );
}

// === Cells =========================================================== //

/**
 * One row's player: avatar and a link to their own card, because the next step
 * of any invite question is there.
 */
function InvitedParty({
  projectId,
  row,
}: {
  projectId: string;
  row: ReferralInvitedPlayer;
}) {
  const gone = row.player_id === null || row.display_name === null;
  return (
    <span className={styles.party}>
      {gone ? (
        <>
          <span
            className={`${styles.goneAvatar} ${styles.goneAvatarSm}`}
            aria-hidden
          >
            ?
          </span>
          <span className={styles.gone}>Deleted player</span>
        </>
      ) : (
        <>
          <Avatar name={row.display_name ?? undefined} size={24} />
          <Link to={`/projects/${projectId}/players/${row.player_id}`}>
            {row.display_name}
          </Link>
        </>
      )}
    </span>
  );
}

/**
 * What THIS player — the inviter — got out of the row.
 *
 * `inviter_granted` is read FIRST, and that is the whole subtlety of this
 * cell: `reward_granted_at` is when the pair was settled, which pays the
 * invited player whatever the ladder says. A settled pair that paid this player
 * nothing — a step in a hole or past the ladder's end, or no ladder at all at
 * the time — is «No reward», and neutral: nothing went wrong, the ladder said
 * nothing.
 */
function RewardPill({ row }: { row: ReferralInvitedPlayer }) {
  if (row.inviter_granted) {
    return (
      <Pill size="sm" variant="success">
        Granted {formatDay(row.reward_granted_at)}
      </Pill>
    );
  }
  return (
    <Pill size="sm" variant="neutral">
      {row.reward_granted_at !== null ? "No reward" : "Waiting"}
    </Pill>
  );
}

// === Loading skeleton ================================================= //

/**
 * Three placeholder rows under the «Invited by» line — the handful a card
 * usually holds, not the twenty it may. A skeleton the height of the maximum
 * would drop the rest of the page out of sight for the time of one request.
 */
function ReferralsSkeleton() {
  return (
    <>
      <div className={styles.skelInviter}>
        <Skeleton width={38} height={38} radius={999} />
        <div className={styles.skelInviterLines}>
          <Skeleton width={64} height={9} />
          <Skeleton width="45%" height={13} style={{ marginTop: 6 }} />
        </div>
        <Skeleton width={120} height={18} radius={999} />
      </div>
      <div className={styles.skelRows}>
        {[0, 1, 2].map((i) => (
          <div className={styles.skelRow} key={i}>
            <Skeleton width={130} height={12} />
            <Skeleton width={92} height={12} />
            <Skeleton width={54} height={12} />
            <Skeleton width={72} height={16} radius={999} />
          </div>
        ))}
      </div>
    </>
  );
}
